import {
  CollateralBasketType,
  EntityStatus,
  Market,
  OrderDetail,
  PreparedOrderClearingDetail,
  RepoType,
  SettlementVenue,
  TradingEntityClearingDetail,
  TradingEntityClearingType
} from 'api'
import { isTriParty } from 'model'

export function getCommonClearingDetails(
  orderDetails: OrderDetail[],
  clearingDetails: TradingEntityClearingDetail[]
): Record<string, PreparedOrderClearingDetail[]> {
  const detailsGroupedByOrder = orderDetails.map(orderDetail => getPreparedOrderClearingDetails(orderDetail, clearingDetails))
  return getCommonPreparedOrderClearingDetails(detailsGroupedByOrder)
}

function getPreparedOrderClearingDetails(
  orderDetail: OrderDetail,
  clearingDetails: TradingEntityClearingDetail[]
): PreparedOrderClearingDetail[] {
  const matchingClearingDetails = matchOrderDetail(orderDetail, clearingDetails)

  return matchingClearingDetails.map(matchingClearingDetail => ({
    orderId: orderDetail.order.orderId,
    clearingHouse: matchingClearingDetail.clearingHouse,
    sponsor:
      matchingClearingDetail.tradingEntityClearing.type === TradingEntityClearingType.SPONSORED ? matchingClearingDetail.sponsor : null,
    swiftMessageId: matchingClearingDetail.tradingEntityClearing.swiftMessageId
  }))
}

function getCommonPreparedOrderClearingDetails(
  detailsGroupedByOrder: PreparedOrderClearingDetail[][]
): Record<string, PreparedOrderClearingDetail[]> {
  const clearingHouseSponsorMap: Record<string, Record<string, PreparedOrderClearingDetail>> = detailsGroupedByOrder
    .reduce((acc, details) => [...acc, ...details], [])
    .reduce((map, preparedOrderClearingDetail) => {
      const sponsorId = preparedOrderClearingDetail.sponsor !== null ? preparedOrderClearingDetail.sponsor.id : ''
      const clearingHouseSponsorKey = `${preparedOrderClearingDetail.clearingHouse!.id}|${sponsorId}`

      // create a map of clearing house + sponsor  -> map of order id -> clearing detail
      // this ensures that there is only one entry per order
      return {
        ...map,
        [clearingHouseSponsorKey]: {
          ...map[clearingHouseSponsorKey],
          [preparedOrderClearingDetail.orderId]: preparedOrderClearingDetail
        }
      }
    }, {} as Record<string, Record<string, PreparedOrderClearingDetail>>)

  // find those clearing house / sponsor combinations which are common to all the orders
  return Object.keys(clearingHouseSponsorMap)
    .filter(
      clearingHouseSponsorKey => Object.keys(clearingHouseSponsorMap[clearingHouseSponsorKey]).length === detailsGroupedByOrder.length
    )
    .map(clearingHouseSponsorKey => clearingHouseSponsorMap[clearingHouseSponsorKey])
    .map(ordersRecord => Object.values(ordersRecord))
    .reduce((acc, ordersRecords) => [...acc, ...ordersRecords], [])
    .reduce(
      (map, preparedOrderClearingDetail) => ({
        ...map,
        [preparedOrderClearingDetail.orderId]: map[preparedOrderClearingDetail.orderId]
          ? [...map[preparedOrderClearingDetail.orderId], preparedOrderClearingDetail]
          : [preparedOrderClearingDetail]
      }),
      {} as Record<string, PreparedOrderClearingDetail[]>
    )
}

function matchOrderDetail(orderDetail: OrderDetail, clearingDetails: TradingEntityClearingDetail[]): TradingEntityClearingDetail[] {
  if (orderDetail.order.fundId === null || (orderDetail.collateral === null && orderDetail.collateralBasket === null)) {
    return []
  }

  // This will become more sophisticated but right now LCH (the only Clearing House supported initially) only clears CLASSIC
  if (orderDetail.order.repoType !== RepoType.CLASSIC) {
    return []
  }

  // US clearing trades do not use trading entity clearing
  if (orderDetail.order.market === Market.US) {
    return []
  }

  return clearingDetails.filter(
    clearingDetail =>
      isTradingEntityClearingValid(clearingDetail, orderDetail) &&
      isOrderCollateralValid(clearingDetail, orderDetail) &&
      isOrderDbvBasketValid(clearingDetail, orderDetail) &&
      isOrderBespokeBasketValid(clearingDetail, orderDetail) &&
      isOrderGCPlusBasketValid(clearingDetail, orderDetail)
  )
}

function isTradingEntityClearingValid(clearingDetail: TradingEntityClearingDetail, orderDetail: OrderDetail) {
  return (
    clearingDetail.clearingHouse !== null &&
    clearingDetail.clearingHouse.status === EntityStatus.ENABLED &&
    clearingDetail.tradingEntityClearing.status === EntityStatus.ENABLED &&
    clearingDetail.tradingEntityClearing.tradingEntityId === orderDetail.order.fundId
  )
}

function isOrderCollateralValid(clearingDetail: TradingEntityClearingDetail, orderDetail: OrderDetail) {
  if (!orderDetail.collateral) {
    return true
  }

  const clearingHouseCollaterals =
    clearingDetail.tradingEntityClearing.type === TradingEntityClearingType.DIRECT
      ? clearingDetail.clearingHouse!.permittedDirectCollateralIds
      : clearingDetail.clearingHouse!.permittedSponsoredCollateralIds

  return clearingHouseCollaterals.includes(orderDetail.collateral.id)
}

function isOrderDbvBasketValid(clearingDetail: TradingEntityClearingDetail, orderDetail: OrderDetail) {
  if (!orderDetail.collateralBasket) {
    return true
  }

  if (!orderDetail.productSector || !orderDetail.productSector.basketType) {
    return false
  }

  if (orderDetail.productSector.basketType !== CollateralBasketType.DBV) {
    return true
  }

  const clearingHouseCollateralBaskets =
    clearingDetail.tradingEntityClearing.type === TradingEntityClearingType.DIRECT
      ? clearingDetail.clearingHouse!.permittedDirectBasketIds
      : clearingDetail.clearingHouse!.permittedSponsoredBasketIds
  return clearingHouseCollateralBaskets.includes(orderDetail.collateralBasket.id)
}

function isOrderGCPlusBasketValid(clearingDetail: TradingEntityClearingDetail, orderDetail: OrderDetail) {
  if (!orderDetail.collateralBasket) {
    return true
  }

  if (!orderDetail.productSector || !orderDetail.productSector.basketType) {
    return false
  }

  if (orderDetail.productSector.basketType !== CollateralBasketType.GC_PLUS) {
    return true
  }

  const clearingHouseCollateralBaskets =
    clearingDetail.tradingEntityClearing.type === TradingEntityClearingType.DIRECT
      ? clearingDetail.clearingHouse!.permittedDirectBasketIds
      : clearingDetail.clearingHouse!.permittedSponsoredBasketIds

  return clearingHouseCollateralBaskets.includes(orderDetail.collateralBasket.id)
}

function isOrderBespokeBasketValid(clearingDetail: TradingEntityClearingDetail, orderDetail: OrderDetail) {
  if (!orderDetail.collateralBasket) {
    return true
  }

  if (!orderDetail.productSector || !orderDetail.productSector.basketType) {
    return false
  }

  if (orderDetail.productSector.basketType !== CollateralBasketType.GC_BESPOKE) {
    return true
  }

  const clearingHouseCollateralBaskets =
    clearingDetail.tradingEntityClearing.type === TradingEntityClearingType.DIRECT
      ? clearingDetail.clearingHouse!.permittedDirectBasketIds
      : clearingDetail.clearingHouse!.permittedSponsoredBasketIds
  const clearingHouseCollaterals =
    clearingDetail.tradingEntityClearing.type === TradingEntityClearingType.DIRECT
      ? clearingDetail.clearingHouse!.permittedDirectCollateralIds
      : clearingDetail.clearingHouse!.permittedSponsoredCollateralIds

  return (
    clearingHouseCollateralBaskets.includes(orderDetail.collateralBasket.id) &&
    orderDetail.collateralBasket.collateralIds.some(collateralId => clearingHouseCollaterals.includes(collateralId))
  )
}

export function getSettlementVenueOptions(
  preparedOrderClearingDetails: PreparedOrderClearingDetail[],
  basketType: CollateralBasketType | null,
  someOrdersAreOpenRepo: boolean,
  someOrdersAreBlockGcBespoke: boolean,
  someOrdersAreCallable: boolean,
  allOrdersAreUs: boolean,
  orderIsStockBorrowingLending: boolean,
  orderIsGCPlus: boolean,
  someOrdersAreCorp: boolean
): string[] {
  if (basketType && isTriParty(basketType)) {
    return [SettlementVenue.Triparty]
  }

  if ((someOrdersAreOpenRepo || someOrdersAreBlockGcBespoke || orderIsStockBorrowingLending || someOrdersAreCorp) && !orderIsGCPlus) {
    return [SettlementVenue.Bilateral]
  }

  const settlementVenueOptions: string[] = orderIsGCPlus ? [] : [SettlementVenue.Bilateral]

  if (someOrdersAreCallable) {
    return settlementVenueOptions
  }

  if (preparedOrderClearingDetails.some(clearingDetail => clearingDetail.sponsor === null)) {
    settlementVenueOptions.push(SettlementVenue.Direct)
  }

  if (preparedOrderClearingDetails.some(clearingDetail => clearingDetail.sponsor !== null) || allOrdersAreUs) {
    settlementVenueOptions.push(SettlementVenue.Sponsored)
  }

  return settlementVenueOptions
}

export const getSettlementVenueOptionsForUsSponsoredClearing = () => [SettlementVenue.Bilateral, SettlementVenue.Sponsored]
